import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TRANSLATION_KEYS } from '@core/internationalization/translations_keys';
import { ToastService } from '@shared/services/toast.service';
import { ModalBgComponent } from '@shared/components/modal-bg/modal-bg.component';
import { TwModalCardComponent } from '@shared/components/tw-modal-card/tw-modal-card.component';
import { Copy, LucideAngularModule } from 'lucide-angular';
import { AgentChatService } from '../../services/agent-chat.service';
import { AgentChatPrefsService } from '../../services/agent-chat-prefs.service';
import { AgentChatSettingsService } from '../../services/agent-chat-settings.service';

/**
 * Chat settings modal (opened by the gear). Two parts: personalization options
 * (persisted via AgentChatPrefsService) and the widget info / embed snippet.
 * Mounted once in the main shell; visibility driven by AgentChatSettingsService.
 */
@Component({
    selector: 'app-agent-chat-settings',
    imports: [TranslateModule, LucideAngularModule, ModalBgComponent, TwModalCardComponent],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './agent-chat-settings.component.html',
    styles: [':host { display: contents; }'],
})
export class AgentChatSettingsComponent {
    protected readonly settingsS = inject(AgentChatSettingsService);
    private readonly prefsS = inject(AgentChatPrefsService);
    private readonly chatS = inject(AgentChatService);
    private readonly toastS = inject(ToastService);
    private readonly translateS = inject(TranslateService);

    protected readonly T = TRANSLATION_KEYS.agentChat;

    protected readonly CopyIcon = Copy;
    protected readonly agents = this.chatS.agents;
    protected readonly prefs = this.prefsS.prefs;

    protected readonly widgetUrl = `${window.location.origin}/widget`;
    protected readonly embedScript = `<script src="${window.location.origin}/assets/widget/twinny-ai.js"></script>`;
    protected readonly embedCode =
        `<iframe src="${this.widgetUrl}" style="border:0;width:400px;height:640px"` +
        ` allow="clipboard-write"></iframe>`;

    protected setDefaultAgent(value: string): void {
        this.prefsS.update({ defaultAgentId: value || null });
    }

    protected setWelcome(value: string): void {
        this.prefsS.update({ welcomeMessage: value });
    }

    protected setLauncherLabel(value: string): void {
        this.prefsS.update({ launcherLabel: value });
    }

    protected async copy(text: string): Promise<void> {
        try {
            await navigator.clipboard.writeText(text);
            this.toastS.success(this.T.settings.copied);
        } catch {
            this.toastS.error(this.translateS.instant(this.T.settings.copyFailed));
        }
    }
}
